import { generateContext } from '@btime/react-contexts'
import { gql } from '@apollo/client'
import React, { useContext } from 'react'

import { compose, propOr, uniqBy, prop, sortBy, chain } from 'ramda'

import { generalClient } from '../../../../../utils/apollo/api'
import { useQrcode } from '../../../_load/QrcodeLoader'
import { useQuery } from '../../../../../utils/apollo/_helpers/useQuery'
import StatePresenter from '../../../../../components/helpers/StatePresenter'

const { StateContext, Provider } = generateContext()
export const useLinkTreeItemsState = () => useContext(StateContext)

// For places, the query always resolves at most one winning linkTree (limit: 1).
// For assets, when `configureAssetQrCodeMenuRules` is enabled the backend may return
// several simultaneously valid linkTrees for the same asset — merge their items,
// dedupe by item id (in case the same item appears via more than one rule) and
// keep the overall ordination.
const useLinkTreeItems = () =>
  compose(
    sortBy(prop('ordination')),
    uniqBy(prop('id')),
    chain(propOr([], 'linkTreeItems'))
  )(useContext(StateContext).linkTrees || [])

export const useLinkTreeItemsWithFormattedUrls = () => {
  const qrcode = useQrcode()
  const items = useLinkTreeItems()
  return items.map(item => {
    if (item.url.includes('btime.io')) {
      let url = item.url
      url += url.includes('?') ? '&' : '?'
      url += qrcode.type === 'assets' ? `assetId=${qrcode?.data?.id}` : `placeId=${qrcode?.data?.id}`
      return { ...item, url }
    }

    return item
  })
}

const QUERY = gql`
  query LinkTrees($filter: LinkTreeFilter, $limit: Int) {
    linkTrees(filter: $filter, limit: $limit) {
      id
      linkTreeItems {
        id
        icon
        url
        label
        ordination
        enabled
        isDefault
        token
        configuration
      }
    }
  }
`

const LinkTreeItemsLoader = ({ children }) => {
  const qrcode = useQrcode()
  const entityId = qrcode?.data?.id
  const isAsset = qrcode.type === 'assets'

  // Places keep the single-winner behavior (limit: 1). Assets, when the backend has
  // `configureAssetQrCodeMenuRules` enabled, may resolve several valid linkTrees at
  // once for the same asset — so we don't cap the result and merge everything below.
  const filter = isAsset ? { firstForAssetId: entityId } : { firstForPlaceId: entityId }
  const limit = isAsset ? undefined : 1

  const { data, ...state } = useQuery(QUERY, {
    variables: { filter, limit },
    client: generalClient
  })

  return (
    <StatePresenter {...state}>
      <Provider value={data}>{children}</Provider>
    </StatePresenter>
  )
}

export default LinkTreeItemsLoader
